import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import { WhatsApp as WhatsAppIcon } from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import SuperAdminLayout from '../components/SuperAdminLayout';
import { getPlatformSettings, updatePlatformSettings } from '../services/platformSettingsService';

/**
 * System-wide settings (Super Admin).
 */
const SettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [whatsappOtpEnabled, setWhatsappOtpEnabled] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getPlatformSettings();
        if (!cancelled) setWhatsappOtpEnabled(!!data.enableWhatsAppRegistrationOtp);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load settings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleToggle = async (_event, checked) => {
    const previous = whatsappOtpEnabled;
    setWhatsappOtpEnabled(checked);
    setSaving(true);
    try {
      await updatePlatformSettings({ enableWhatsAppRegistrationOtp: checked });
      toast.success(checked ? 'WhatsApp OTP enabled for registration' : 'WhatsApp OTP disabled — tenants can register without OTP');
    } catch (e) {
      setWhatsappOtpEnabled(previous);
      toast.error(e.response?.data?.message || e.message || 'Could not save setting');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SuperAdminLayout>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600, color: '#2c3e50' }}>
            System Settings
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Configure system-wide settings for all tenants and registration flows.
          </Typography>
        </Box>

        <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              <Box display="flex" alignItems="flex-start" gap={2}>
                <WhatsAppIcon color="primary" sx={{ mt: 0.5 }} />
                <Box flex={1}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    WhatsApp OTP on company registration
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
                    When enabled, new companies must verify their mobile number with a WhatsApp OTP before continuing.
                    Turn this off if your WhatsApp template is not approved yet — registrants can enter a mobile number and go to the next step without verification (same as the older flow).
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={whatsappOtpEnabled}
                        onChange={handleToggle}
                        disabled={saving}
                        color="primary"
                      />
                    }
                    label={whatsappOtpEnabled ? 'Enabled' : 'Disabled'}
                  />
                  {saving && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      Saving…
                    </Typography>
                  )}
                </Box>
              </Box>
            </>
          )}
        </Paper>
      </Container>
    </SuperAdminLayout>
  );
};

export default SettingsPage;
